import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { TeamService, Team } from '../../services/team.service';
import VaultSidebar from '../../components/vault/VaultSidebar';
import VaultHeader from '../../components/vault/VaultHeader';
import Button from '../../components/common/Button';
import { ArrowLeftIcon, ArrowsUpDownIcon, ChevronDownIcon, ArrowDownIcon, ArrowUpIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { useAppSelector } from '../../hooks/useRedux';
import AddMemberModal from '../../components/team/AddMemberModal';
import InviteMemberModal from '../../components/team/InviteMemberModal';
import ActiveUsersList from '../../components/team/ActiveUsersList';
import PendingUsersList from '../../components/team/PendingUsersList';
import { Role, TeamMember } from '../../types/team.types';
import { hasPermission } from '../../utils/permissionUtils';
import ConfirmModal from '../../components/common/ConfirmModal';

// Sort options type
type SortOption = 'newest' | 'alphabetical';

const TeamDetailsPage: React.FC = () => {
  const { teamId } = useParams<{ teamId: string }>();
  const navigate = useNavigate();
  const [team, setTeam] = useState<Team | null>(null);
  const [currentTeam, setCurrentTeam] = useState<Team | null>(null);
  const [activeTab, setActiveTab] = useState('active');
  const [loading, setLoading] = useState(true);
  const [showAddMemberModal, setShowAddMemberModal] = useState(false);
  const [showInviteMemberModal, setShowInviteMemberModal] = useState(false);
  const { permissions, role_id, team_id } = useAppSelector(state => state.auth);
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [invitations, setInvitations] = useState([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [isDepartment, setIsDepartment] = useState(false);

  // Sorting states
  const [sortOption, setSortOption] = useState<SortOption>('newest');
  const [isSortMenuOpen, setIsSortMenuOpen] = useState(false);

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  const isAppAdmin = role_id === 1;

  const [showRoleConfirmModal, setShowRoleConfirmModal] = useState(false);
  const [selectedRoleUpdate, setSelectedRoleUpdate] = useState<{
    userId: string;
    newRoleId: number;
    currentRole?: string;
    newRole?: string;
  } | null>(null);
  const [isUpdatingRole, setIsUpdatingRole] = useState(false);

  const fetchTeamData = async () => {
    if (!teamId) return;
    console.log("teamId", teamId)

    try {
      setLoading(true);

      // Fetch team details
      const teamResponse = await TeamService.getTeamById(teamId);
      setTeam(teamResponse.team);

      // Determine if this is a department or organization
      const isTeamDepartment = teamResponse.team?.team_type === 'department' ||
        !!teamResponse.team?.parent_team_id;
      setIsDepartment(isTeamDepartment);

      // Fetch team members and invitations in a single call
      const membersResponse = await TeamService.getTeamMembers(teamId);
      setMembers(membersResponse.activeMembers || []);
      setInvitations(membersResponse.pendingInvitations || []);
      setRoles(membersResponse.roles || []);
      setCurrentTeam(teamResponse.team || null);
    } catch (error) {
      console.error('Error fetching team data:', error);
      toast.error('Failed to load team data');
    } finally {
      setLoading(false);
    }
  };

  const handleRoleChange = async (userId: string, newRoleId: number) => {
    const currentRole = roles.find(r => r.id === members.find(m => m.id === userId)?.role_id)?.name;
    const newRole = roles.find(r => r.id === newRoleId)?.name;
    
    setSelectedRoleUpdate({
      userId,
      newRoleId,
      currentRole,
      newRole
    });
    setShowRoleConfirmModal(true);
  };

  const confirmRoleUpdate = async () => {
    if (!selectedRoleUpdate) return;

    try {
      setIsUpdatingRole(true);
      // Check if the member is a guest user
      const member = members.find(m => m.user_id === selectedRoleUpdate.userId);
      const isGuestUser = member?.team_id !== team_id;
      
      const response = await TeamService.updateUserRole(
        selectedRoleUpdate.userId, 
        selectedRoleUpdate.newRoleId,
        team_id || '',
        isGuestUser
      );
      
      if (response.success) {
        toast.success('Role updated successfully');
        fetchTeamData(); // Refresh the team members after a successful update
      } else {
        toast.error('Failed to update role: ' + response.message);
      }
    } catch (error) {
      console.error('Error updating role:', error);
      toast.error('Error updating role');
    } finally {
      setIsUpdatingRole(false);
      setShowRoleConfirmModal(false);
      setSelectedRoleUpdate(null);
    }
  };

  useEffect(() => {
    fetchTeamData();
  }, [teamId]);

  const getSortedMembers = (members: any[]): any[] => {
    const sorted = [...members];
    switch (sortOption) {
      case 'newest':
        return sorted.sort((a, b) => new Date(b.joined_at).getTime() - new Date(a.joined_at).getTime());
      case 'alphabetical':
        return sorted.sort((a, b) => (a.email || '').localeCompare(b.email || ''));
      default:
        return sorted;
    }
  };

  const getSortedInvitations = (invitations: any[]): any[] => {
    const sorted = [...invitations];
    switch (sortOption) {
      case 'newest':
        return sorted.sort((a, b) => new Date(b.invited_at).getTime() - new Date(a.invited_at).getTime());
      case 'alphabetical':
        return sorted.sort((a, b) => (a.email || '').localeCompare(b.email || ''));
      default:
        return sorted;
    }
  };

  const SortMenu = () => {
    return (
      <div className="relative">
        <button
          onClick={() => setIsSortMenuOpen(!isSortMenuOpen)}
          className="flex items-center px-3 py-2 bg-white border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          <ArrowsUpDownIcon className="h-5 w-5 mr-1 text-gray-500" />
          Sort
          <ChevronDownIcon className="h-4 w-4 ml-1" />
        </button>

        {isSortMenuOpen && (
          <div className="absolute mt-2 right-0 w-48 bg-white shadow-lg rounded-md overflow-hidden z-10 border border-gray-200">
            <div className="py-1">
              <button
                className={`flex items-center w-full px-4 py-2 text-sm ${sortOption === 'newest' ? 'bg-gray-100 text-gray-900' : 'text-gray-700'}`}
                onClick={() => {
                  setSortOption('newest');
                  setIsSortMenuOpen(false);
                }}
              >
                Newest
                {sortOption === 'newest' && <ArrowDownIcon className="h-4 w-4 ml-2" />}
              </button>
              <button
                className={`flex items-center w-full px-4 py-2 text-sm ${sortOption === 'alphabetical' ? 'bg-gray-100 text-gray-900' : 'text-gray-700'}`}
                onClick={() => {
                  setSortOption('alphabetical');
                  setIsSortMenuOpen(false);
                }}
              >
                Alphabetical (A-Z)
                {sortOption === 'alphabetical' && <ArrowUpIcon className="h-4 w-4 ml-2" />}
              </button>
            </div>
          </div>
        )}
      </div>
    );
  };

  const Pagination = ({ totalItems }: { totalItems: number }) => {
    const totalPages = Math.ceil(totalItems / itemsPerPage);
    
    return (
      <div className="flex items-center justify-center px-4 py-3 mt-1 border-gray-200 sm:px-6">
        <div className="flex justify-center flex-1">
          <button
            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
            disabled={currentPage === 1}
            className="relative inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
          >
            Previous
          </button>
          <div className="mx-2">
            {[...Array(totalPages)].map((_, i) => (
              <button
                key={i + 1}
                onClick={() => setCurrentPage(i + 1)}
                className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                  currentPage === i + 1
                    ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                    : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                }`}
              >
                {i + 1}
              </button>
            ))}
          </div>
          <button
            onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
            disabled={currentPage === totalPages}
            className="relative inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
          >
            Next
          </button>
        </div>
      </div>
    );
  };

  const isTeamAdmin = team?.role_id === 2 || isAppAdmin;
  const canManageTeam = hasPermission(permissions, 'team', 'invite_members');
  const entityType = isDepartment ? 'Team' : 'Organization';

  return (
    <div className="h-screen flex overflow-hidden bg-gray-100">
      <VaultSidebar />
      <div className="flex flex-col flex-1 overflow-hidden">
        <VaultHeader />
        <main className="flex-1 relative overflow-y-auto focus:outline-none">
          <div className="py-6">
            <div className="w-full mx-auto px-4 sm:px-6 md:px-8">
              {/* Header with back button and team name */}
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center">
                  <button
                    onClick={() => navigate(-1)}
                    className="mr-4 p-2 rounded-full hover:bg-gray-200"
                  >
                    <ArrowLeftIcon className="h-5 w-5 text-gray-600" />
                  </button>
                  <div>
                    <h1 className="text-2xl font-semibold text-gray-900">
                      {loading ? 'Loading...' : team?.name}
                    </h1>
                    {!loading && (
                      <div className="text-sm text-gray-500">
                        Team Members
                      </div>
                    )}
                  </div>
                </div>
                
                  <div className="flex space-x-3">
                    <SortMenu />
                    {canManageTeam && (
                      <>
                        <Button
                          variant="secondary"
                      onClick={() => setShowAddMemberModal(true)}
                    >
                      Add Member
                    </Button>
                    <Button
                      variant="primary"
                      onClick={() => setShowInviteMemberModal(true)}
                    >
                        Invite Member
                      </Button>
                    </>
                  )}
                </div>
              </div>

              {loading ? (
                <div className="animate-pulse">
                  <div className="h-8 bg-gray-200 rounded w-1/4 mb-4"></div>
                  <div className="h-64 bg-gray-200 rounded"></div>
                </div>
              ) : (
                <>
                  {/* Tab Navigation */}
                  <div className="border-b border-gray-200 mb-6">
                    <nav className="-mb-px flex space-x-8">
                      <button
                        onClick={() => setActiveTab('active')}
                        className={`${activeTab === 'active'
                            ? 'border-blue-500 text-blue-600'
                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                          } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
                      >
                        Active Members ({members.length})
                      </button>
                      <button
                        onClick={() => setActiveTab('pending')}
                        className={`${activeTab === 'pending'
                            ? 'border-blue-500 text-blue-600'
                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                          } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
                      >
                        Pending Invitations ({invitations.length})
                      </button>
                    </nav>
                  </div>

                  {/* Content Area */}
                  <div className="mt-6">
                    {activeTab === 'active' ? (
                      <ActiveUsersList
                        members={getSortedMembers(members)}
                        teamId={teamId || ''}
                        roles={roles}
                        onRoleChange={handleRoleChange}
                        onRevoke={fetchTeamData}
                        onMemberRemoved={fetchTeamData}
                        entityType={entityType}
                      />
                    ) : (
                      <PendingUsersList
                        invitations={getSortedInvitations(invitations)}
                        onRevoke={fetchTeamData}
                        onOpenInviteModal={() => setShowInviteMemberModal(true)}
                        entityType={entityType}
                      />
                    )}
                  </div>
                  <Pagination totalItems={activeTab === 'active' ? members.length : invitations.length} />
                </>
              )}
            </div>
          </div>
        </main>
      </div>

      {/* Modals */}
      {showAddMemberModal && (
        <AddMemberModal
          isOpen={true}
          teamId={teamId || ''}
          roles={roles || []}
          onClose={() => setShowAddMemberModal(false)}
          onSuccess={fetchTeamData}
          entityType={entityType}
        />
      )}

      {showInviteMemberModal && (
        <InviteMemberModal
          isOpen={true}
          teamId={teamId || ''}
          team_id={team_id || ''}
          team={currentTeam || null}
          teamName={team?.name}
          onClose={() => setShowInviteMemberModal(false)}
          onInviteSuccess={fetchTeamData}
          entityType={entityType}
          useStaticRoles={true}
        />
      )}

      {showRoleConfirmModal && selectedRoleUpdate && (
        <ConfirmModal
          isOpen={showRoleConfirmModal}
          title="Update Role"
          message={`Are you sure you want to change this user's role to "${selectedRoleUpdate.newRole}"?`}
          confirmText="Update"
          cancelButtonText="Cancel"
          isLoading={isUpdatingRole}
          onConfirm={confirmRoleUpdate}
          onCancel={() => {
            setShowRoleConfirmModal(false);
            setSelectedRoleUpdate(null);
          }}
        />
      )}
    </div>
  );
};

export default TeamDetailsPage;